// « Messages » (#/messages, #/messages?fil=<coach>): list of the coach threads (real names and portraits, demo contents), then a
// full-screen thread built on the UBM ChatThread (adapted copy, src/adapted/chat-thread). White received bubbles, blue sent
// bubbles, composer at the bottom with « + » (the UBM DropdownMenu, the « + » being its real trigger: « Photo ou vidéo » / « Document »), local
// previews above the field, context card (« À propos de … ») removable before sending. Session-local demo: nothing is sent.
import { useEffect, useRef, useState, type CSSProperties } from "react";
import { ChevronLeft, ChevronRight, FileText, ImageIcon } from "lucide-react";
import { Avatar } from "@ubm/ui/components/avatar";
import { DropdownMenuContentBase, DropdownMenuItem, DropdownMenuPortal, DropdownMenuRoot, DropdownMenuTrigger, dropdownMenuClassNames as menu, useMenuHighlight } from "@ubm/ui/components/dropdown-menu";
import { ChatThread, type ChatThreadHandle, type ChatThreadLabels } from "./adapted/chat-thread/chat-thread";
import { StatusBar, type Route } from "./parts";
import { AccountSheet } from "./profil";
import { ShellActions } from "./shell";
import { fullName, useAccount } from "./compte-state";
import { COACHES, ME, lastAt, messagerie, useMessagerie, type CoachId } from "./messagerie-state";
import { useShellImmersive } from "./shell-state";

interface PageProps { go: (route: Route) => void; onOutOfScope: (label: string) => void }

const LABELS: ChatThreadLabels = {
  send: "Envoyer", sending: "Envoi…", stop: "Arrêter", sendFailed: "Envoi impossible. Ton message est conservé.",
  attach: "Ajouter une pièce jointe", attachments: "Pièces jointes", removeFile: n => `Retirer ${n}`,
  sent: "Envoyé", delivered: "Envoyé", read: "Envoyé", failed: "Non envoyé (démo)", retry: "Réessayer",
  readBy: () => "", typing: n => `${n[0] ?? ""} écrit…`, newMessages: c => `${c} nouveau${c > 1 ? "x" : ""} message${c > 1 ? "s" : ""}`,
  jumpToLatest: "Derniers messages", unknownAuthor: "Inconnu", addReaction: "Réagir", reactions: "Réactions",
  reactWith: e => `Réagir avec ${e}`, reactionCount: (e, c, you) => `${e} ${c}${you ? ", dont toi" : ""}`,
};
const DOC_ACCEPT = ".pdf,.doc,.docx,.odt,.rtf,.txt,.xls,.xlsx,.ods,.ppt,.pptx,.odp";
const readFil = (): CoachId | null => { const m = window.location.hash.match(/fil=(alexandre|antoine)/); return m ? m[1] as CoachId : null; };
const when = (t: number) => {
  const d = new Date(t), now = new Date();
  return d.toDateString() === now.toDateString() ? d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : d.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
};

export function Messagerie({ go }: PageProps) {
  const [fil, setFil] = useState<CoachId | null>(readFil);
  useEffect(() => { const on = () => setFil(readFil()); window.addEventListener("hashchange", on); return () => window.removeEventListener("hashchange", on); }, []);
  return fil ? <Thread key={fil} coachId={fil} /> : <List go={go} />;
}

/** The coach threads (same rows as before), shared by this page and the shell's bell drawer; `onOpen` opens the full thread. */
export function ThreadRows({ onOpen }: { onOpen: (coach: CoachId) => void }) {
  const s = useMessagerie();
  const rows = [...COACHES].sort((a, b) => lastAt(s.messages[b.id]) - lastAt(s.messages[a.id]));
  return <ul className="cc-card cc-messages-list">{rows.map(c => {
        const list = s.messages[c.id], last = list[list.length - 1], unread = s.unread[c.id];
        const preview = !last ? "Aucun message pour l'instant" : `${last.authorId === ME ? "Toi : " : ""}${last.text ?? (last.attachments?.length ? "Pièce jointe" : "")}`;
        return <li key={c.id}><button type="button" className="cc-messages-row" data-unread={unread > 0 || undefined} onClick={() => onOpen(c.id)}>
          <Avatar name={c.name} src={c.photo} size="md" />
          <span className="cc-messages-row-text"><span className="cc-messages-row-top"><b>{c.name}</b>{last && <time>{when(new Date(last.createdAt).getTime())}</time>}</span>
            <span className="cc-messages-row-preview">{preview}</span></span>
          {unread > 0 ? <span className="cc-messages-badge" aria-label={`${unread} non lu${unread > 1 ? "s" : ""}`}>{unread}</span> : <ChevronRight size={16} color="#c4c4c9" aria-hidden="true" />}
        </button></li>;
      })}</ul>;
}

function List({ go }: { go: (r: Route) => void }) {
  return <div className="cc-screen cc-messages">
    <StatusBar />
    <div className="cc-scroll">
      <header className="cc-account-head">
        <button type="button" className="cc-icon-btn" aria-label="Retour à l'accueil" onClick={() => go("accueil")}><ChevronLeft size={18} strokeWidth={2} /></button>
        <h1 className="cc-title">Messages</h1>
        <ShellActions />
      </header>
      <ThreadRows onOpen={id => { window.location.hash = `#/messages?fil=${id}`; }} />
      <p className="cc-home-demo cc-account-demo">Prototype : messages de démonstration, rien n'est envoyé.</p>
    </div>
  </div>;
}

function Thread({ coachId }: { coachId: CoachId }) {
  useShellImmersive(true); // full message thread: focused view, no dock
  const s = useMessagerie();
  const a = useAccount();
  const coach = COACHES.find(c => c.id === coachId)!;
  const thread = useRef<ChatThreadHandle>(null);
  const photoInput = useRef<HTMLInputElement>(null);
  const docInput = useRef<HTMLInputElement>(null);
  // « + » menu: vendored DropdownMenu (Radix), the composer's « + » wrapped as its trigger; keyboard, outside press, Escape and
  // focus return are the component's. Same composition and classes as the vendor's own DropdownMenu, source highlight included.
  const highlight = useMenuHighlight();
  const [preview, setPreview] = useState<{ file: File; url?: string } | null>(null);
  useEffect(() => { messagerie.markRead(coachId); }, [coachId, s.messages[coachId].length]);
  const context = s.context[coachId];
  const pick = (files: FileList | null) => { if (files?.length) thread.current?.addFiles(Array.from(files)); setAttachOpen(false); };
  const empty = s.messages[coachId].length === 0;

  return <div className="cc-screen cc-messages cc-messages-thread">
    <StatusBar />
    <header className="cc-messages-head">
      <button type="button" className="cc-icon-btn" aria-label="Retour aux messages" onClick={() => { window.location.hash = "#/messages"; }}><ChevronLeft size={18} strokeWidth={2} /></button>
      <Avatar name={coach.name} src={coach.photo} size="sm" />
      <span className="cc-messages-head-text"><b>{coach.name}</b><small>{coach.role}</small></span>
      {/* Apple pass 13/10: the same header actions as every page (bell 22 / 44, avatar 36 / 44), no compact variant in the thread. */}
      <ShellActions />
    </header>
    {empty && <p className="cc-messages-empty">Écris ton premier message à {coach.name.split(" ")[0]}.</p>}
    <div className="cc-messages-chat">
      <ChatThread ref={thread} className="cc-messages-chatthread" locale="fr-FR" label={`Conversation avec ${coach.name}`} labels={LABELS}
        participants={[{ id: ME, name: fullName(a), avatar: a.photo }, { id: coach.id, name: coach.name, avatar: coach.photo }]} currentUserId={ME}
        messages={s.messages[coachId]} placeholder="Ton message…" onSend={d => messagerie.send(coachId, d.text, d.files)} onRetry={id => messagerie.retry(coachId, id)}
        attachTriggerRender={button => <DropdownMenuRoot onOpenChange={open => { if (open) highlight.reset(); }}>
          <DropdownMenuTrigger asChild>{button}</DropdownMenuTrigger>
          <DropdownMenuPortal><DropdownMenuContentBase className={`${menu.menu} cc-messages-attach-menu`} side="top" align="start" sideOffset={6} collisionPadding={12} loop aria-label="Ajouter une pièce jointe" {...highlight.panelProps}>
            {highlight.element}
            <DropdownMenuItem className={menu.item} style={{ "--i": 0 } as CSSProperties} onSelect={() => photoInput.current?.click()}><span className={menu.icon} aria-hidden="true"><ImageIcon size={16} strokeWidth={1.75} /></span>Photo ou vidéo</DropdownMenuItem>
            <DropdownMenuItem className={menu.item} style={{ "--i": 1 } as CSSProperties} onSelect={() => docInput.current?.click()}><span className={menu.icon} aria-hidden="true"><FileText size={16} strokeWidth={1.75} /></span>Document</DropdownMenuItem>
          </DropdownMenuContentBase></DropdownMenuPortal>
        </DropdownMenuRoot>} onPreview={(file, url) => setPreview({ file, url })}
        composerTop={context && <div className="cc-messages-context" role="note">
          <a href={context.href}>{context.label}</a>
          <button type="button" onClick={() => messagerie.clearContext(coachId)}>Retirer ce contexte</button>
        </div>} />
    </div>
    <input ref={photoInput} type="file" accept="image/*,video/*" multiple hidden onChange={e => { pick(e.target.files); e.target.value = ""; }} />
    <input ref={docInput} type="file" accept={DOC_ACCEPT} multiple hidden onChange={e => { pick(e.target.files); e.target.value = ""; }} />
    {preview && <AccountSheet title={preview.file.name} onClose={() => setPreview(null)}>
      {preview.file.type.startsWith("video/") ? <video className="cc-messages-preview" src={preview.url} controls playsInline autoFocus />
        : <img className="cc-messages-preview" src={preview.url} alt={preview.file.name} />}
    </AccountSheet>}
  </div>;
}
